import { useState, useMemo, useId } from 'react';
import {
  Box,
  HStack,
  IconButton,
  VStack,
  Text,
  Input,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { HiTrash, HiPlus, HiExclamationTriangle } from 'react-icons/hi2';
import { Button as ButtonComponent, SectionCard } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useReunificationSite } from '../../hooks/queries/useReunification';
import { useUsers } from '../../hooks/queries/useUsers';
import type {
  StationMapping,
  ZoneMapping,
  ConcreteStaffAssignment,
  PlanFloorplan,
  StaffRole,
} from '../../types/ReunificationTypes';

// =============================================================================
// Shared helpers
// =============================================================================

const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  incident_commander: 'Incident Commander',
  greeter: 'Greeter',
  checker: 'Checker (ID Verification)',
  reunifier: 'Reunifier',
  class_leader: 'Class Leader',
  accountant: 'Accountant (Recorder)',
  leo_liaison: 'LEO Liaison',
  crisis_counselor: 'Crisis Counselor',
  bus_coordinator: 'Bus Coordinator',
  runner: 'Runner',
  releaser: 'Releaser',
  recorder: 'Recorder',
  scribe: 'Scribe',
  medical: 'Medical Staff',
  counselor: 'Counselor',
  coordinator: 'Coordinator',
  other: 'Other',
};

interface SelectOption {
  value: string;
  label: string;
  annotation?: string;
}

interface OptionGroup {
  label: string;
  options: SelectOption[];
}

function SearchableSelect({
  options,
  groups,
  value,
  onChange,
  placeholder = 'Select...',
  ariaLabel,
  hasError,
}: {
  options?: SelectOption[];
  groups?: OptionGroup[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name for the combobox input. Falls back to the
   *  stable `placeholder` prop — never the selected value. */
  ariaLabel?: string;
  hasError?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  // Index into the flat visible-option list for keyboard
  // navigation (aria-activedescendant pattern — focus stays on the input).
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();

  const allOptions = useMemo(
    () => (groups ? groups.flatMap((g) => g.options) : options ?? []),
    [options, groups],
  );

  const lowerSearch = search.toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!groups) return null;
    if (!search) return groups;
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) =>
          o.label.toLowerCase().includes(lowerSearch),
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, search, lowerSearch]);

  const filteredOptions = useMemo(() => {
    if (groups) return null;
    const opts = options ?? [];
    return search
      ? opts.filter((o) => o.label.toLowerCase().includes(lowerSearch))
      : opts;
  }, [options, groups, search, lowerSearch]);

  // Flat view of what the listbox currently shows, in render order —
  // the keyboard navigates this list.
  const visibleFlat = useMemo(
    () =>
      filteredGroups
        ? filteredGroups.flatMap((g) => g.options)
        : filteredOptions ?? [],
    [filteredGroups, filteredOptions],
  );

  const selectedLabel = allOptions.find((o) => o.value === value)?.label;

  const closeList = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const selectOption = (opt: SelectOption) => {
    onChange(opt.value);
    setSearch('');
    closeList();
  };

  const optionDomId = (flatIdx: number) => `${listboxId}-opt-${flatIdx}`;

  const renderOption = (opt: SelectOption, flatIdx: number) => (
    <Box
      key={opt.value}
      id={optionDomId(flatIdx)}
      role="option"
      aria-selected={opt.value === value}
      px={3}
      py={2}
      cursor="pointer"
      bg={
        flatIdx === activeIndex
          ? 'bg.subtle'
          : opt.value === value
            ? 'bg.muted'
            : undefined
      }
      _hover={{ bg: 'bg.subtle' }}
      onMouseDown={(e) => {
        e.preventDefault();
        selectOption(opt);
      }}
    >
      <HStack gap={2}>
        <Text fontSize="sm">{opt.label}</Text>
        {opt.annotation && (
          <Text fontSize="xs" color="yellow.600">
            {opt.annotation}
          </Text>
        )}
      </HStack>
    </Box>
  );

  const hasResults = groups
    ? (filteredGroups?.length ?? 0) > 0
    : (filteredOptions?.length ?? 0) > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, visibleFlat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && activeIndex < visibleFlat.length) {
        e.preventDefault();
        selectOption(visibleFlat[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setSearch('');
        closeList();
      }
    }
  };

  return (
    <Box position="relative">
      <Input
        size="sm"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeIndex >= 0 ? optionDomId(activeIndex) : undefined
        }
        aria-label={ariaLabel || placeholder}
        placeholder={selectedLabel || placeholder}
        value={open ? search : selectedLabel || ''}
        onChange={(e) => {
          setSearch(e.target.value);
          setActiveIndex(-1);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(closeList, 200)}
        onKeyDown={handleKeyDown}
        borderColor={hasError ? 'border.danger' : undefined}
      />
      {open && (
        <Box
          id={listboxId}
          role="listbox"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={10}
          bg="surface.primary"
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          {!hasResults ? (
            <Text fontSize="xs" color="text.secondary" p={2}>
              No results
            </Text>
          ) : groups && filteredGroups ? (
            (() => {
              // Running flat index across groups so aria-activedescendant
              // and renderOption agree with visibleFlat's order.
              let flatIdx = 0;
              return filteredGroups.map((group) => (
                <Box key={group.label} role="group" aria-label={group.label}>
                  <Text
                    aria-hidden="true"
                    fontSize="xs"
                    fontWeight="600"
                    color="text.secondary"
                    px={3}
                    py={1.5}
                    bg="bg.subtle"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    {group.label}
                  </Text>
                  {group.options.map((opt) => renderOption(opt, flatIdx++))}
                </Box>
              ));
            })()
          ) : (
            filteredOptions?.map((opt, i) => renderOption(opt, i))
          )}
        </Box>
      )}
    </Box>
  );
}

function FieldError({ message }: { message: string }) {
  return (
    <Text fontSize="xs" color="text.danger">
      {message}
    </Text>
  );
}

function FeatureWarning({ message }: { message: string }) {
  return (
    <HStack gap={1}>
      <Box color="yellow.500" flexShrink={0}>
        <HiExclamationTriangle size={12} />
      </Box>
      <Text fontSize="xs" color="yellow.600">
        {message}
      </Text>
    </HStack>
  );
}

function useConfirmClose(isDirty: boolean, onClose: () => void) {
  return () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) {
      return;
    }
    onClose();
  };
}

// =============================================================================
// Station Mappings Drawer
// =============================================================================

interface StationMappingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mappings: StationMapping[];
  floorplans: PlanFloorplan[];
  siteId: string;
  onSave: (mappings: StationMapping[]) => void;
  isSaving: boolean;
  assignedFeatureIds?: Set<string>;
  crossTypeLabel?: string;
}

export function StationMappingsDrawer({
  isOpen,
  onClose,
  mappings,
  floorplans,
  siteId,
  onSave,
  isSaving,
  assignedFeatureIds,
  crossTypeLabel = 'zones',
}: StationMappingsDrawerProps) {
  const [draft, setDraft] = useState<StationMapping[]>([]);
  const { data: siteDetail, isLoading: isSiteLoading } =
    useReunificationSite(siteId);

  // Reset draft when drawer opens
  const [lastOpen, setLastOpen] = useState(false);
  if (isOpen && !lastOpen) {
    setDraft([...mappings]);
    setLastOpen(true);
  } else if (!isOpen && lastOpen) {
    setLastOpen(false);
  }

  const allStations = useMemo(
    () => floorplans.flatMap((fp) => fp.stations),
    [floorplans],
  );

  const featureGroups = useMemo(
    () => buildRoomGroups(siteDetail?.rooms ?? []),
    [siteDetail],
  );

  // Validation
  const validationErrors = useMemo(() => {
    const errors = new Map<number, string[]>();
    const seenStations = new Map<string, number>();
    const seenFeatures = new Map<string, number>();

    draft.forEach((m, i) => {
      const errs: string[] = [];
      if (!m.plan_station_public_id) errs.push('Station is required');
      if (!m.room_public_id) errs.push('Site feature is required');

      if (m.plan_station_public_id) {
        const prev = seenStations.get(m.plan_station_public_id);
        if (prev !== undefined) {
          errs.push('Duplicate station');
          const prevErrs = errors.get(prev) ?? [];
          if (!prevErrs.includes('Duplicate station')) {
            errors.set(prev, [...prevErrs, 'Duplicate station']);
          }
        }
        seenStations.set(m.plan_station_public_id, i);
      }

      if (m.room_public_id) {
        const prev = seenFeatures.get(m.room_public_id);
        if (prev !== undefined) {
          errs.push('Duplicate feature in this list');
          const prevErrs = errors.get(prev) ?? [];
          if (!prevErrs.includes('Duplicate feature in this list')) {
            errors.set(prev, [...prevErrs, 'Duplicate feature in this list']);
          }
        }
        seenFeatures.set(m.room_public_id, i);
      }

      if (errs.length) errors.set(i, errs);
    });
    return errors;
  }, [draft]);

  const hasErrors = validationErrors.size > 0;

  // Cross-type warnings
  const crossTypeWarnings = useMemo(() => {
    if (!assignedFeatureIds) return new Map<number, string>();
    const warnings = new Map<number, string>();
    draft.forEach((m, i) => {
      if (m.room_public_id && assignedFeatureIds.has(m.room_public_id)) {
        warnings.set(i, `Also used in ${crossTypeLabel}`);
      }
    });
    return warnings;
  }, [draft, assignedFeatureIds, crossTypeLabel]);

  // Dirty state
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(mappings),
    [draft, mappings],
  );
  const handleClose = useConfirmClose(isDirty, onClose);

  // Feature annotations for dropdown
  const annotateFeatureGroups = useMemo(() => {
    const draftFeatureIds = new Set(
      draft.map((m) => m.room_public_id).filter(Boolean),
    );
    return featureGroups.map((g) => ({
      ...g,
      options: g.options.map((o) => ({
        ...o,
        annotation:
          draftFeatureIds.has(o.value)
            ? '[assigned]'
            : assignedFeatureIds?.has(o.value)
              ? `[${crossTypeLabel}]`
              : undefined,
      })),
    }));
  }, [featureGroups, draft, assignedFeatureIds, crossTypeLabel]);

  const updateMapping = (index: number, updates: Partial<StationMapping>) => {
    setDraft((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    );
  };

  const deleteMapping = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addMapping = () => {
    // Find first unmapped station
    const mappedIds = new Set(draft.map((m) => m.plan_station_public_id));
    const unmapped = allStations.find((s) => !mappedIds.has(s.public_id));
    setDraft((prev) => [
      ...prev,
      {
        plan_station_public_id: unmapped?.public_id ?? '',
        room_public_id: '',
        notes: '',
      },
    ]);
  };

  const stationOptions = useMemo(
    () =>
      allStations.map((s) => ({
        value: s.public_id,
        label: `${s.name} (${s.station_type_display})`,
      })),
    [allStations],
  );

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Station Mappings"
      size="lg"
      closeOnOutside={false}
      closeOnEsc={!isDirty}
      footer={
        <HStack gap={4} width="100%">
          <ButtonComponent visual="secondary" type="button" onClick={handleClose} data-e2e="cancel-station-mappings-button">
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => onSave(draft)}
            disabled={isSaving || hasErrors}
            data-e2e="save-station-mappings-button"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </ButtonComponent>
        </HStack>
      }
    >
      <SectionCard
        title="Station mappings"
        action={
          <ButtonComponent
            visual="secondary"
            size="sm"
            type="button"
            onClick={addMapping}
            data-e2e="add-station-mapping-button"
          >
            <HiPlus size={14} /> Add Station Mapping
          </ButtonComponent>
        }
      >
        {isSiteLoading ? (
          <Center py={8}>
            <Spinner size="sm" />
          </Center>
        ) : (
          <VStack gap={3} align="stretch">
            {draft.map((mapping, index) => {
              const errors = validationErrors.get(index);
              const warning = crossTypeWarnings.get(index);
              return (
                <Box
                  key={index}
                  p={3}
                  borderWidth="1px"
                  borderColor={errors ? 'red.300' : 'border.primary'}
                  borderRadius="md"
                >
                  <VStack gap={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="xs" fontWeight="600" color="text.secondary">
                        Station
                      </Text>
                      <IconButton
                        aria-label={`Remove station mapping ${index + 1}`}
                        size="xs"
                        variant="ghost"
                        color="text.danger"
                        onClick={() => deleteMapping(index)}
                        _hover={{ color: 'red.600' }}
                      >
                        <HiTrash size={14} />
                      </IconButton>
                    </HStack>
                    <SearchableSelect
                      options={stationOptions}
                      value={mapping.plan_station_public_id}
                      onChange={(v) =>
                        updateMapping(index, { plan_station_public_id: v })
                      }
                      placeholder="Select plan station..."
                      ariaLabel={`Station — mapping ${index + 1}`}
                      hasError={errors?.some((e) => e.includes('Station') || e.includes('station'))}
                    />
                    <Text fontSize="xs" fontWeight="600" color="text.secondary">
                      Site Feature
                    </Text>
                    <SearchableSelect
                      groups={annotateFeatureGroups}
                      value={mapping.room_public_id}
                      onChange={(v) =>
                        updateMapping(index, { room_public_id: v })
                      }
                      placeholder="Select site feature..."
                      ariaLabel={`Site Feature — mapping ${index + 1}`}
                      hasError={errors?.some((e) => e.includes('feature') || e.includes('Feature'))}
                    />
                    {warning && <FeatureWarning message={warning} />}
                    <Input
                      size="sm"
                      aria-label={`Notes — mapping ${index + 1}`}
                      placeholder="Notes (optional)"
                      value={mapping.notes}
                      onChange={(e) =>
                        updateMapping(index, { notes: e.target.value })
                      }
                    />
                    {errors?.map((err) => (
                      <FieldError key={err} message={err} />
                    ))}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </SectionCard>
    </DrawerComponent>
  );
}

// =============================================================================
// Zone Mappings Drawer
// =============================================================================

interface ZoneMappingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mappings: ZoneMapping[];
  floorplans: PlanFloorplan[];
  siteId: string;
  onSave: (mappings: ZoneMapping[]) => void;
  isSaving: boolean;
  assignedFeatureIds?: Set<string>;
  crossTypeLabel?: string;
}

export function ZoneMappingsDrawer({
  isOpen,
  onClose,
  mappings,
  floorplans,
  siteId,
  onSave,
  isSaving,
  assignedFeatureIds,
  crossTypeLabel = 'stations',
}: ZoneMappingsDrawerProps) {
  const [draft, setDraft] = useState<ZoneMapping[]>([]);
  const { data: siteDetail, isLoading: isSiteLoading } =
    useReunificationSite(siteId);

  const [lastOpen, setLastOpen] = useState(false);
  if (isOpen && !lastOpen) {
    setDraft([...mappings]);
    setLastOpen(true);
  } else if (!isOpen && lastOpen) {
    setLastOpen(false);
  }

  const allZones = useMemo(
    () => floorplans.flatMap((fp) => fp.zones),
    [floorplans],
  );

  const featureGroups = useMemo(
    () => buildRoomGroups(siteDetail?.rooms ?? []),
    [siteDetail],
  );

  // Validation
  const validationErrors = useMemo(() => {
    const errors = new Map<number, string[]>();
    const seenZones = new Map<string, number>();
    const seenFeatures = new Map<string, number>();

    draft.forEach((m, i) => {
      const errs: string[] = [];
      if (!m.plan_zone_public_id) errs.push('Zone is required');
      if (!m.room_public_id) errs.push('Site feature is required');

      if (m.plan_zone_public_id) {
        const prev = seenZones.get(m.plan_zone_public_id);
        if (prev !== undefined) {
          errs.push('Duplicate zone');
          const prevErrs = errors.get(prev) ?? [];
          if (!prevErrs.includes('Duplicate zone')) {
            errors.set(prev, [...prevErrs, 'Duplicate zone']);
          }
        }
        seenZones.set(m.plan_zone_public_id, i);
      }

      if (m.room_public_id) {
        const prev = seenFeatures.get(m.room_public_id);
        if (prev !== undefined) {
          errs.push('Duplicate feature in this list');
          const prevErrs = errors.get(prev) ?? [];
          if (!prevErrs.includes('Duplicate feature in this list')) {
            errors.set(prev, [...prevErrs, 'Duplicate feature in this list']);
          }
        }
        seenFeatures.set(m.room_public_id, i);
      }

      if (errs.length) errors.set(i, errs);
    });
    return errors;
  }, [draft]);

  const hasErrors = validationErrors.size > 0;

  // Cross-type warnings
  const crossTypeWarnings = useMemo(() => {
    if (!assignedFeatureIds) return new Map<number, string>();
    const warnings = new Map<number, string>();
    draft.forEach((m, i) => {
      if (m.room_public_id && assignedFeatureIds.has(m.room_public_id)) {
        warnings.set(i, `Also used in ${crossTypeLabel}`);
      }
    });
    return warnings;
  }, [draft, assignedFeatureIds, crossTypeLabel]);

  // Dirty state
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(mappings),
    [draft, mappings],
  );
  const handleClose = useConfirmClose(isDirty, onClose);

  // Feature annotations for dropdown
  const annotateFeatureGroups = useMemo(() => {
    const draftFeatureIds = new Set(
      draft.map((m) => m.room_public_id).filter(Boolean),
    );
    return featureGroups.map((g) => ({
      ...g,
      options: g.options.map((o) => ({
        ...o,
        annotation:
          draftFeatureIds.has(o.value)
            ? '[assigned]'
            : assignedFeatureIds?.has(o.value)
              ? `[${crossTypeLabel}]`
              : undefined,
      })),
    }));
  }, [featureGroups, draft, assignedFeatureIds, crossTypeLabel]);

  const zoneOptions = useMemo(
    () =>
      allZones.map((z) => ({
        value: z.public_id,
        label: `${z.name} (${z.zone_type_display})`,
      })),
    [allZones],
  );

  const updateMapping = (index: number, updates: Partial<ZoneMapping>) => {
    setDraft((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    );
  };

  const deleteMapping = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addMapping = () => {
    const mappedIds = new Set(draft.map((m) => m.plan_zone_public_id));
    const unmapped = allZones.find((z) => !mappedIds.has(z.public_id));
    setDraft((prev) => [
      ...prev,
      {
        plan_zone_public_id: unmapped?.public_id ?? '',
        room_public_id: '',
        notes: '',
      },
    ]);
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Zone Mappings"
      size="lg"
      closeOnOutside={false}
      closeOnEsc={!isDirty}
      footer={
        <HStack gap={4} width="100%">
          <ButtonComponent visual="secondary" type="button" onClick={handleClose} data-e2e="cancel-zone-mappings-button">
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => onSave(draft)}
            disabled={isSaving || hasErrors}
            data-e2e="save-zone-mappings-button"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </ButtonComponent>
        </HStack>
      }
    >
      <SectionCard
        title="Zone mappings"
        action={
          <ButtonComponent
            visual="secondary"
            size="sm"
            type="button"
            onClick={addMapping}
            data-e2e="add-zone-mapping-button"
          >
            <HiPlus size={14} /> Add Zone Mapping
          </ButtonComponent>
        }
      >
        {isSiteLoading ? (
          <Center py={8}>
            <Spinner size="sm" />
          </Center>
        ) : (
          <VStack gap={3} align="stretch">
            {draft.map((mapping, index) => {
              const errors = validationErrors.get(index);
              const warning = crossTypeWarnings.get(index);
              return (
                <Box
                  key={index}
                  p={3}
                  borderWidth="1px"
                  borderColor={errors ? 'red.300' : 'border.primary'}
                  borderRadius="md"
                >
                  <VStack gap={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="xs" fontWeight="600" color="text.secondary">
                        Zone
                      </Text>
                      <IconButton
                        aria-label={`Remove zone mapping ${index + 1}`}
                        size="xs"
                        variant="ghost"
                        color="text.danger"
                        onClick={() => deleteMapping(index)}
                        _hover={{ color: 'red.600' }}
                      >
                        <HiTrash size={14} />
                      </IconButton>
                    </HStack>
                    <SearchableSelect
                      options={zoneOptions}
                      value={mapping.plan_zone_public_id}
                      onChange={(v) =>
                        updateMapping(index, { plan_zone_public_id: v })
                      }
                      placeholder="Select plan zone..."
                      ariaLabel={`Zone — mapping ${index + 1}`}
                      hasError={errors?.some((e) => e.includes('Zone') || e.includes('zone'))}
                    />
                    <Text fontSize="xs" fontWeight="600" color="text.secondary">
                      Site Feature
                    </Text>
                    <SearchableSelect
                      groups={annotateFeatureGroups}
                      value={mapping.room_public_id}
                      onChange={(v) =>
                        updateMapping(index, { room_public_id: v })
                      }
                      placeholder="Select site feature..."
                      ariaLabel={`Site Feature — mapping ${index + 1}`}
                      hasError={errors?.some((e) => e.includes('feature') || e.includes('Feature'))}
                    />
                    {warning && <FeatureWarning message={warning} />}
                    <Input
                      size="sm"
                      aria-label={`Notes — mapping ${index + 1}`}
                      placeholder="Notes (optional)"
                      value={mapping.notes}
                      onChange={(e) =>
                        updateMapping(index, { notes: e.target.value })
                      }
                    />
                    {errors?.map((err) => (
                      <FieldError key={err} message={err} />
                    ))}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </SectionCard>
    </DrawerComponent>
  );
}

// =============================================================================
// Staff Assignments Drawer
// =============================================================================

interface StaffAssignmentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  assignments: ConcreteStaffAssignment[];
  floorplans: PlanFloorplan[];
  onSave: (assignments: ConcreteStaffAssignment[]) => void;
  isSaving: boolean;
}

export function StaffAssignmentsDrawer({
  isOpen,
  onClose,
  assignments,
  floorplans,
  onSave,
  isSaving,
}: StaffAssignmentsDrawerProps) {
  const [draft, setDraft] = useState<ConcreteStaffAssignment[]>([]);

  const [lastOpen, setLastOpen] = useState(false);
  if (isOpen && !lastOpen) {
    setDraft([...assignments]);
    setLastOpen(true);
  } else if (!isOpen && lastOpen) {
    setLastOpen(false);
  }

  const allStations = useMemo(
    () => floorplans.flatMap((fp) => fp.stations),
    [floorplans],
  );

  const stationOptions = useMemo(
    () => [
      { value: '', label: 'None' },
      ...allStations.map((s) => ({
        value: s.public_id,
        label: `${s.name} (${s.station_type_display})`,
      })),
    ],
    [allStations],
  );

  const roleOptions = useMemo(
    () =>
      Object.entries(STAFF_ROLE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    [],
  );

  // Validation
  const validationErrors = useMemo(() => {
    const errors = new Map<number, string[]>();
    draft.forEach((a, i) => {
      const errs: string[] = [];
      if (!a.user_id) errs.push('Staff member is required');
      if (!a.role) errs.push('Role is required');
      if (errs.length) errors.set(i, errs);
    });
    return errors;
  }, [draft]);

  const hasErrors = validationErrors.size > 0;

  // Dirty state
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(assignments),
    [draft, assignments],
  );
  const handleClose = useConfirmClose(isDirty, onClose);

  const updateAssignment = (
    index: number,
    updates: Partial<ConcreteStaffAssignment>,
  ) => {
    setDraft((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...updates } : a)),
    );
  };

  const deleteAssignment = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addAssignment = () => {
    setDraft((prev) => [
      ...prev,
      {
        user_id: 0,
        role: 'other' as StaffRole,
        station_public_id: null,
        confirmed: false,
      },
    ]);
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Staff Assignments"
      size="lg"
      closeOnOutside={false}
      closeOnEsc={!isDirty}
      footer={
        <HStack gap={4} width="100%">
          <ButtonComponent visual="secondary" type="button" onClick={handleClose} data-e2e="cancel-button">
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => onSave(draft)}
            disabled={isSaving || hasErrors}
            data-e2e="issaving--saving--save-button">
            {isSaving ? 'Saving...' : 'Save'}
          </ButtonComponent>
        </HStack>
      }
    >
      <SectionCard
        title="Staff assignments"
        action={
          <ButtonComponent
            visual="secondary"
            size="sm"
            type="button"
            onClick={addAssignment}
          >
            <HiPlus size={14} /> Add Staff Assignment
          </ButtonComponent>
        }
      >
        <VStack gap={3} align="stretch">
          {draft.map((assignment, index) => {
            const errors = validationErrors.get(index);
            return (
              <Box
                key={index}
                p={3}
                borderWidth="1px"
                borderColor={errors ? 'red.300' : 'border.primary'}
                borderRadius="md"
              >
                <VStack gap={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="xs" fontWeight="600" color="text.secondary">
                      Staff Member
                    </Text>
                    <IconButton
                      aria-label={`Remove staff assignment ${index + 1}`}
                      size="xs"
                      variant="ghost"
                      color="text.danger"
                      onClick={() => deleteAssignment(index)}
                      _hover={{ color: 'red.600' }}
                    >
                      <HiTrash size={14} />
                    </IconButton>
                  </HStack>
                  <UserSearchSelect
                    value={assignment.user_id}
                    onChange={(userId) =>
                      updateAssignment(index, { user_id: userId })
                    }
                    ariaLabel={`Staff Member — assignment ${index + 1}`}
                  />
                  <Text fontSize="xs" fontWeight="600" color="text.secondary">
                    Role
                  </Text>
                  <SearchableSelect
                    options={roleOptions}
                    value={assignment.role}
                    onChange={(v) =>
                      updateAssignment(index, { role: v as StaffRole })
                    }
                    placeholder="Select role..."
                    ariaLabel={`Role — assignment ${index + 1}`}
                    hasError={errors?.some((e) => e.includes('Role'))}
                  />
                  <Text fontSize="xs" fontWeight="600" color="text.secondary">
                    Station (optional)
                  </Text>
                  <SearchableSelect
                    options={stationOptions}
                    value={assignment.station_public_id ?? ''}
                    onChange={(v) =>
                      updateAssignment(index, {
                        station_public_id: v || null,
                      })
                    }
                    placeholder="No station"
                    ariaLabel={`Station (optional) — assignment ${index + 1}`}
                  />
                  {errors?.map((err) => (
                    <FieldError key={err} message={err} />
                  ))}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </SectionCard>
    </DrawerComponent>
  );
}

// =============================================================================
// User Search Select (for staff picker)
// =============================================================================

function UserSearchSelect({
  value,
  onChange,
  ariaLabel,
}: {
  value: number;
  onChange: (userId: number) => void;
  /** Accessible name for the search input — should contain the
   *  visible label text next to the control (WCAG 2.5.3). */
  ariaLabel: string;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { data: usersData, isLoading } = useUsers({
    search: search || undefined,
    is_active: true,
    page_size: 20,
  });

  const users = usersData?.results ?? [];

  // Same aria-activedescendant combobox pattern as
  // SearchableSelect — focus stays on the input, arrows move the
  // active option, Enter selects, Escape closes.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const optionDomId = (i: number) => `${listboxId}-opt-${i}`;

  const closeList = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const selectUser = (user: (typeof users)[number]) => {
    const userId = Number(user.id);
    onChange(userId);
    setSearch(`${user.first_name} ${user.last_name}`.trim() || user.email);
    closeList();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, users.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && activeIndex < users.length) {
        e.preventDefault();
        selectUser(users[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        closeList();
      }
    }
  };

  return (
    <Box position="relative">
      <Input
        size="sm"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeIndex >= 0 ? optionDomId(activeIndex) : undefined
        }
        aria-label={ariaLabel}
        placeholder={value ? `User #${value}` : 'Search users...'}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setActiveIndex(-1);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(closeList, 200)}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <Box
          id={listboxId}
          role="listbox"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={10}
          bg="surface.primary"
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          {isLoading ? (
            <Center py={2}>
              <Spinner size="xs" />
            </Center>
          ) : users.length === 0 ? (
            <Text fontSize="xs" color="text.secondary" p={2}>
              {search ? 'No users found' : 'Type to search users'}
            </Text>
          ) : (
            users.map((user, i) => {
              const userId = Number(user.id);
              return (
                <Box
                  key={user.id}
                  id={optionDomId(i)}
                  role="option"
                  aria-selected={userId === value}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={
                    i === activeIndex
                      ? 'bg.subtle'
                      : userId === value
                        ? 'bg.muted'
                        : undefined
                  }
                  _hover={{ bg: 'bg.subtle' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectUser(user);
                  }}
                >
                  <Text fontSize="sm">
                    {user.first_name} {user.last_name}
                  </Text>
                  <Text fontSize="xs" color="text.secondary">
                    {user.email}
                  </Text>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
}

// =============================================================================
// Shared utilities
// =============================================================================

/**
 * Build grouped options from rooms for station/zone mapping selectors.
 * Groups rooms by room_type (stored in data.room_type on schools.Room).
 * Falls back to 'Uncategorized' for rooms without a type.
 */
function buildRoomGroups(rooms: Array<{ public_id: string; name: string; room_type?: string }>): OptionGroup[] {
  const groups = new Map<string, SelectOption[]>();
  rooms.forEach((r) => {
    const key = r.room_type || 'Uncategorized';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push({ value: r.public_id, label: r.name });
  });
  return Array.from(groups, ([label, options]) => ({
    label,
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
  })).sort((a, b) => a.label.localeCompare(b.label));
}
